import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { IconProps } from '@brighterly/lib-lumee-ui'
import type { BlockSectionLayoutProps } from '@ts-blocks/block-section-layout/interfaces'
import type { SectionRatingsGaugeProps } from '@ts-sections/section-ratings-gauge/interfaces'
import type { BrandShapeIconColor, BrandShapeIconShape } from '@UI/Brand/BrandShapeIcon'

export interface RatingCriterionVisual {
  icon: IconProps['name']
  shape: BrandShapeIconShape
  color: BrandShapeIconColor
}

const CRITERIA_VISUALS: RatingCriterionVisual[] = [
  { icon: 'book-open-reader', shape: 'square', color: 'ultraviolet' },
  { icon: 'star', shape: 'hexagon', color: 'sky' },
  { icon: 'user', shape: 'circle', color: 'sunshine' },
  { icon: 'microphone', shape: 'speech-bubble', color: 'pink' },
  { icon: 'graduation-cap', shape: 'scallop', color: 'green' },
]

interface SectionRatingsGaugeHookOptions {
  section: () => SectionRatingsGaugeProps
}

interface SectionRatingsGaugeHookReturn {
  refTitle: ComputedRef<string>
  refDescription: ComputedRef<string>
  refBackgroundColor: ComputedRef<NonNullable<BlockSectionLayoutProps['backgroundColor']>>
  getCriterionVisual: (index: number) => RatingCriterionVisual
}

export const useSectionRatingsGauge = ({ section }: SectionRatingsGaugeHookOptions): SectionRatingsGaugeHookReturn => {
  const refLayout = computed<BlockSectionLayoutProps | null>(() => section().layout ?? null)

  const refTitle = computed<string>(() => refLayout.value?.title ?? '')
  const refDescription = computed<string>(() => refLayout.value?.description ?? '')

  const refBackgroundColor = computed<NonNullable<BlockSectionLayoutProps['backgroundColor']>>(() => (
    refLayout.value?.backgroundColor ?? 'tertiary'
  ))

  const getCriterionVisual = (index: number): RatingCriterionVisual => (
    CRITERIA_VISUALS[index % CRITERIA_VISUALS.length] as RatingCriterionVisual
  )

  return {
    refTitle,
    refDescription,
    refBackgroundColor,
    getCriterionVisual,
  }
}
